import { WebsiteImage } from '@flipdish/api-client-typescript';
import {
  ExpansionPanel,
  ExpansionPanelDetails,
  ExpansionPanelSummary,
  FormControl,
  FormHelperText,
  Grid,
  makeStyles,
} from '@material-ui/core';
import Switch from '@material-ui/core/Switch';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import { Field, FieldProps } from 'formik';
import React, { memo, useCallback, useEffect, useState } from 'react';
import { Translate } from 'react-localize-redux';
import { connect } from 'react-redux';

import { usePrevious } from '../../../custom-hooks/usePrevious';
import { formikValidate } from '../../../helpers/validation';
import { createLoadingSelector } from '../../../selectors/loading.selector';
import Button from '../../../ui/Button/Button';
import Spacer from '../../../ui/Spacer';
import {
  DELETE_WEBSITE_SETTINGS_IMAGE,
  deleteWebsiteSettingsImage,
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  uploadWebsiteSettingsImage,
} from '../actions';
import EditWebsiteSettingsUploadImage from './EditWebsiteSettingsUploadImage';
import usePanelHandler, { PanelProps } from './usePanelHandler';
import { handleImage } from './utils';

const savingSelector = createLoadingSelector([
  UPLOAD_WEBSITE_SETTINGS_IMAGE,
  DELETE_WEBSITE_SETTINGS_IMAGE,
]);

const useStyles = makeStyles({
  header: {
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  content: {
    alignItems: 'center',
    flexDirection: 'column',
  },
  section: {
    width: '100%',
  },
  removeButtonWrapper: {
    textAlign: 'center',
  },
  removeButton: {
    fontSize: '10px',
    cursor: 'pointer',
    textDecoration: 'underline',
  },
});

const mapStateToProps = (state: AppState) => {
  return {
    saving: savingSelector(state),
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch) => ({
  uploadImage: (imageLocation: WebsiteImage.ImageLocationEnum, file) =>
    dispatch(uploadWebsiteSettingsImage(imageLocation, file)),
  deleteImage: (id: number) => dispatch(deleteWebsiteSettingsImage(id)),
});

type Props = PanelProps & {
  initialImage?: WebsiteImage;
} & ReturnType<typeof mapDispatchToProps> &
  ReturnType<typeof mapStateToProps>;

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(
  memo(function EditWebsiteSettingsAboutSectionsPanel(props: Props) {
    const {
      name,
      onToggle,
      expanded,
      onExpand,
      initialImage,
      uploadImage,
      deleteImage,
      saving,
      className,
    } = props;
    const classes = useStyles();
    const [image, setImage] = useState<WebsiteImage | File | undefined>(initialImage);
    const [error, setError] = useState<string>();
    const { handleExpanding } = usePanelHandler(name, onExpand);
    const previousImage = usePrevious(initialImage);
    const handleChange = useCallback((files: FileList | null) => {
      if (files) {
        setError(formikValidate.extension(files, ['png', 'jpeg', 'jpg']));
      } else {
        setError(undefined);
      }
      setImage(files ? files[0] : undefined);
    }, []);
    const handleSave = useCallback(() => {
      handleImage(
        uploadImage,
        deleteImage,
        WebsiteImage.ImageLocationEnum.IndexOpeningHoursHeader,
        image,
        previousImage
      );
    }, [image, previousImage]);

    useEffect(() => {
      setImage(initialImage);
    }, [initialImage]);

    return (
      <ExpansionPanel className={className} onChange={handleExpanding} expanded={expanded}>
        <ExpansionPanelSummary
          classes={{ content: classes.header }}
          expandIcon={<ExpandMoreIcon />}
          IconButtonProps={{ 'data-fd': 'opening-hours-toggle' }}
          aria-controls="opening_hours"
        >
          <Translate id="Opening_hours_title" />
          <Field name="OpeningHoursEnabled">
            {(fProps: FieldProps) => {
              const { field, form } = fProps;

              return (
                <Switch
                  value=""
                  color="default"
                  data-fd="website_settings_opening_hours_toggle"
                  checked={field.value}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e, value: boolean) => {
                    form.setFieldValue(field.name, value);
                    if (onToggle) {
                      onToggle(name);
                    }
                  }}
                />
              );
            }}
          </Field>
        </ExpansionPanelSummary>
        <ExpansionPanelDetails className={classes.content}>
          <EditWebsiteSettingsUploadImage
            image={image && image instanceof WebsiteImage ? image.ImageUrl : undefined}
            fdKey="opening_hours"
            onChange={handleChange}
          />
          {error ? (
            <FormControl error fullWidth>
              <FormHelperText>{error}</FormHelperText>
            </FormControl>
          ) : null}
          <Spacer size={8} />
          <Grid container justify="flex-end">
            <Button
              variant="outlined"
              color="primary"
              fdKey="save_opening_hours"
              disabled={saving}
              onClick={handleSave}
            >
              <Translate id="Save" />
            </Button>
          </Grid>
        </ExpansionPanelDetails>
      </ExpansionPanel>
    );
  })
);
